import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { RiArrowDownSLine, RiCheckLine, RiGlobalLine } from 'react-icons/ri';
import { LANGUAGES, getLanguageMeta } from '../../i18n/languages';
import { getLangFromPathname, stripLangPrefix, withLangPrefix } from '../../i18n/pathUtils';
import './LanguageSwitcher.css';

const LanguageSwitcher = ({ variant = 'header' }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const listboxId = useId();
    const rootRef = useRef(null);
    const [open, setOpen] = useState(false);

    const active = getLangFromPathname(location.pathname);
    const activeMeta = getLanguageMeta(active);

    const changeTo = useCallback(
        (code) => {
            if (code === active) {
                setOpen(false);
                return;
            }

            const basePath = stripLangPrefix(location.pathname);
            const target = withLangPrefix(code, basePath);

            try {
                localStorage.setItem('i18nextLng', code);
            } catch (e) {
                /* storage may be unavailable (private mode) — ignore */
            }

            navigate(`${target}${location.search}${location.hash}`);
            setOpen(false);
        },
        [active, location.hash, location.pathname, location.search, navigate]
    );

    useEffect(() => {
        if (!open) return undefined;

        const handlePointerDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                setOpen(false);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    return (
        <div
            ref={rootRef}
            className={`lang-dropdown lang-dropdown--${variant}${open ? ' is-open' : ''}`}
        >
            <button
                type="button"
                className="lang-dropdown__trigger"
                aria-label={t('common.languageSwitcher.label')}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={listboxId}
                onClick={() => setOpen((prev) => !prev)}
            >
                <RiGlobalLine className="lang-dropdown__icon" aria-hidden="true" />
                <span className="lang-dropdown__current">{activeMeta.short}</span>
                <RiArrowDownSLine className="lang-dropdown__chevron" aria-hidden="true" />
            </button>

            <ul
                id={listboxId}
                className="lang-dropdown__menu"
                role="listbox"
                aria-label={t('common.languageSwitcher.label')}
            >
                {LANGUAGES.map((language) => {
                    const isActive = language.code === active;

                    return (
                        <li key={language.code} role="presentation">
                            <button
                                type="button"
                                role="option"
                                lang={language.htmlLang}
                                aria-selected={isActive}
                                className={`lang-dropdown__option${
                                    isActive ? ' is-active' : ''
                                }`}
                                onClick={() => changeTo(language.code)}
                            >
                                <span className="lang-dropdown__label">
                                    {t(`common.languageSwitcher.${language.code}`)}
                                </span>
                                {isActive && (
                                    <RiCheckLine
                                        className="lang-dropdown__check"
                                        aria-hidden="true"
                                    />
                                )}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default LanguageSwitcher;
